import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'i18n'
import * as styles from '../SideBySideVideo.module.scss'

import { BaseVideoConfig, CampaignConfig } from '../config'
import { TrackFn } from '../SideBySideVideoPage'
import StepHeader from './StepHeader'
import { PauseIcon, PlayIcon } from 'lucide-react'

type Props = {
  config: CampaignConfig
  selected: BaseVideoConfig
  userVideo: Blob
  onBack: () => void
  onRerecord: () => void
  onNext: () => void
  onEvent: TrackFn
}


const fmt = (s: number): string => {
  if (!Number.isFinite(s) || s < 0) s = 0
  const m = Math.floor(s / 60)
  const sec = Math.floor(s % 60)
  return `${m}:${String(sec).padStart(2, '0')}`
}

const fixWebmDuration = (v: HTMLVideoElement) => {
  if (Number.isFinite(v.duration) && v.duration > 0) return
  v.currentTime = 1e9
  const onSeeked = () => {
    v.currentTime = 0
    v.removeEventListener('seeked', onSeeked)
  }
  v.addEventListener('seeked', onSeeked)
}

const PreviewStage = ({
  config,
  selected,
  userVideo,
  onBack,
  onRerecord,
  onNext,
  onEvent,
}: Props) => {
  const { copy, branding } = config
  const { t } = useTranslation(['sideBySideVideo', 'common'])
  const baseRef = useRef<HTMLVideoElement | null>(null)
  const userRef = useRef<HTMLVideoElement | null>(null)
  const baseAudioRef = useRef<HTMLAudioElement | null>(null)

  const checkboxes = copy.previewCheckboxes ?? []

  const [userUrl, setUserUrl] = useState<string | null>(null)
  const [playing, setPlaying] = useState(false)
  const [current, setCurrent] = useState(0)
  const [duration, setDuration] = useState(selected.durationSeconds)
  const [checked, setChecked] = useState<Record<string, boolean>>({})
  const [ready, setReady] = useState(false)

  // All required checkboxes must be ticked before Save & Continue is enabled.
  const allRequiredChecked = checkboxes
    .filter(c => c.required)
    .every(c => checked[c.id])

  useEffect(() => {
    const u = URL.createObjectURL(userVideo)
    setUserUrl(u)
    return () => URL.revokeObjectURL(u)
  }, [userVideo])

  // Force all media to load metadata so iOS allows play() inside gesture.
  useEffect(() => {
    const b = baseRef.current
    const u = userRef.current
    const a = baseAudioRef.current
    if (!b || !u) return
    let baseReady = b.readyState >= 1
    let userReady = u.readyState >= 1
    const check = () => {
      if (baseReady && userReady) setReady(true)
    }
    const onBase = () => { baseReady = true; check() }
    const onUser = () => { userReady = true; check() }
    b.addEventListener('loadedmetadata', onBase)
    u.addEventListener('loadedmetadata', onUser)
    try { b.load(); u.load(); a?.load() } catch { /* ignore */ }
    check()
    return () => {
      b.removeEventListener('loadedmetadata', onBase)
      u.removeEventListener('loadedmetadata', onUser)
    }
  }, [userUrl])

  // Sync user video to base video. Base = master.
  useEffect(() => {
    const b = baseRef.current
    const u = userRef.current
    if (!b || !u || !userUrl) return

    const onPlay = () => {
      // togglePlay already calls u.play() and baseAudio.play()
      setPlaying(true)
    }
    const onPause = () => {
      u.pause()
      baseAudioRef.current?.pause()
      setPlaying(false)
    }
    const onTimeUpdate = () => {
      setCurrent(b.currentTime)
      // Re-sync user video if drift >300ms
      if (Math.abs(u.currentTime - b.currentTime) > 0.3) {
        u.currentTime = b.currentTime
      }
      // Re-sync base audio if drift >300ms
      const a = baseAudioRef.current
      if (a && Math.abs(a.currentTime - b.currentTime) > 0.3) {
        a.currentTime = b.currentTime
      }
    }
    const onEnded = () => {
      u.pause()
      baseAudioRef.current?.pause()
      setPlaying(false)
    }
    const onLoaded = () => {
      if (Number.isFinite(b.duration) && b.duration > 0) {
        setDuration(b.duration)
      }
    }

    b.addEventListener('play', onPlay)
    b.addEventListener('pause', onPause)
    b.addEventListener('timeupdate', onTimeUpdate)
    b.addEventListener('ended', onEnded)
    b.addEventListener('loadedmetadata', onLoaded)

    return () => {
      b.removeEventListener('play', onPlay)
      b.removeEventListener('pause', onPause)
      b.removeEventListener('timeupdate', onTimeUpdate)
      b.removeEventListener('ended', onEnded)
      b.removeEventListener('loadedmetadata', onLoaded)
    }
  }, [userUrl])

  const togglePlay = () => {
    const b = baseRef.current
    const u = userRef.current
    if (!b) return
    if (b.paused) {
      onEvent('step_interaction_sbsv_preview_play')
      // iOS Safari: ALL plays MUST be called inside the same user gesture.
      const plays = [
        b.play(),
        u ? u.play() : null,
        baseAudioRef.current ? baseAudioRef.current.play() : null,
      ]
      Promise.all(plays.map(p => p?.catch(() => {})))
    } else {
      b.pause()
      u?.pause()
      baseAudioRef.current?.pause()
    }
  }

  const seek = (e: React.MouseEvent<HTMLDivElement>) => {
    const b = baseRef.current
    const u = userRef.current
    if (!b || !u) return
    const rect = e.currentTarget.getBoundingClientRect()
    const ratio = (e.clientX - rect.left) / rect.width
    const t = Math.max(0, Math.min(duration, ratio * duration))
    b.currentTime = t
    u.currentTime = t
    if (baseAudioRef.current) baseAudioRef.current.currentTime = t
    setCurrent(t)
  }

  if (!userUrl) return null

  return (
    <>
      <StepHeader step={3} total={4} onBack={onBack} />

      <div style={{ width: '100%' }}>
        <h2 className={styles.title}>{copy.previewTitle}</h2>
        <p className={styles.subtitle}>{copy.previewSubtitle}</p>
      </div>

      <div
        className={[
          styles.sbs,
          config.layout === 'stacked' ? styles.sbsStacked : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {/* Hidden audio element carries base video audio — iOS allows audio+video combo */}
        <audio ref={baseAudioRef} src={selected.src} crossOrigin="anonymous" preload="auto" style={{ display: 'none' }} />
        <video ref={baseRef} src={selected.src} crossOrigin="anonymous" playsInline preload="auto" muted />
        <video
          ref={userRef}
          src={userUrl}
          playsInline
          preload="auto"
          onLoadedMetadata={e => {
            const v = e.currentTarget
            fixWebmDuration(v)
            // iOS won't paint a non-muted video's first frame until it plays —
            // nudge a tiny seek so the right pane isn't blank before Play.
            if (Number.isFinite(v.duration) && v.duration > 0 && v.currentTime === 0) {
              try {
                v.currentTime = 0.05
              } catch {
                /* ignore */
              }
            }
          }}
        />
        {config.branding.overlayImageSrc && (
          <img
            src={config.branding.overlayImageSrc}
            alt=""
            className={styles.sbsOverlay}
          />
        )}
        {!playing && (
          <button
            type="button"
            className={`${styles.outputOverlay} ${styles.outputOverlayBtn}`}
            onClick={togglePlay}
            aria-label={t('sideBySideVideo:media.playPreview')}
            disabled={!ready}
          >
            {ready ? (
              <>
                <PlayIcon size={20} /> {t('sideBySideVideo:preview.playCta')}
              </>
            ) : (
              t('common:loading')
            )}
          </button>
        )}
      </div>

      <div className={styles.playerControls}>
        <div className={styles.playerTimeline} onClick={seek}>
          <div
            className={styles.playerTimelineFill}
            style={{
              width: `${duration ? (current / duration) * 100 : 0}%`,
            }}
          />
        </div>
        <div className={styles.playerTime}>
          <span>{fmt(current)}</span>
          <span>{fmt(duration)}</span>
        </div>
        <button
          type="button"
          className={styles.playerPlay}
          onClick={togglePlay}
          aria-label={
            playing
              ? t('sideBySideVideo:media.pause')
              : t('sideBySideVideo:media.play')
          }
        >
          {playing ? <PauseIcon size={20} /> : <PlayIcon size={20} />}
        </button>
      </div>

      {checkboxes.map(cb => (
        <label key={cb.id} className={styles.legal}>
          <input
            type="checkbox"
            checked={!!checked[cb.id]}
            onChange={e =>
              setChecked(prev => ({ ...prev, [cb.id]: e.target.checked }))
            }
          />
          <span dangerouslySetInnerHTML={{ __html: cb.label }} />
        </label>
      ))}

      <div className={styles.actions}>
        <button
          type="button"
          className={styles.btn}
          disabled={!allRequiredChecked}
          onClick={() => {
            onEvent('step_interaction_sbsv_preview_confirmed')
            onNext()
          }}
        >
          {copy.previewCta}
        </button>
        <button
          type="button"
          className={`${styles.btn} ${styles.btnGhost}`}
          onClick={() => {
            onEvent('step_interaction_sbsv_rerecord')
            onRerecord()
          }}
        >
          {copy.previewRerecordText || t('sideBySideVideo:preview.rerecordCta')}
        </button>
      </div>
    </>
  )
}

export default PreviewStage
